import React, { forwardRef, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../services/knowledge-api';

import { SORT_TYPES } from '../../../constants/sort';
import { CONTENT_TYPE } from '../../../constants/docs';

import {
    handleExpandSidePanelFolder,
    handleUpdateFoldersState,
    toggleSidePanelFolderExpand,
} from '../../../helpers/docsSidePanelUtils';
import { setSyncTimeout } from '../../../helpers/setSyncTimeout';

import { useOverlayState } from '../../../hooks/useOverlayState';
import { useDocsPageContext } from '../../../hooks/useDocsPageContext';

import Alert from '../../../design-system/Alert/Alert';
import Loading from '../../../components/Loading';
import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import CheckLineIcon from '../../../design-system/Icons/CheckLineIcon';
import FolderFillIcon from '../../../design-system/Icons/FolderFillIcon';
import ArrowRightSLineIcon from '../../../design-system/Icons/ArrowRightSLineIcon';
import DraggableFolderNavItem from '../DraggableFolderNavItem/DraggableFolderNavItem';
import DocCardMoreOptionsPopup from '../DocCardMoreOptionsPopup/DocCardMoreOptionsPopup';
import { SvgIcon } from '../../../design-system';
import { ArrowDownSLineIcon, More2FillIcon } from '../../../design-system/Icons';

const FolderNavItem = (
    {
        folder,
        currentFolderExpandedState,
        isTopLevel,
        prevFoldersLocation,
        isDndOver,
        isDragging,
        setActionModal,
    },
    ref
) => {
    const { id, label, folders, is_parent } = folder;
    const navigate = useNavigate();

    const { folderId: folderIdFromParams } = useParams();

    const {
        isOpened: isMoreOptionsPopupOpened,
        open: openMoreOptions,
        close: closeMoreOptions,
    } = useOverlayState();

    const [successAlert, setSuccessAlert] = useState(null);
    const [errorAlert, setErrorAlert] = useState(null);

    const isExpanded = !!currentFolderExpandedState;
    const Icon = isExpanded ? ArrowDownSLineIcon : ArrowRightSLineIcon;

    const { setExpandedFolders, setLocation, setSidePanelFolders } = useDocsPageContext();
    const toggleFolderExpand = (location) => {
        toggleSidePanelFolderExpand({ location, setExpandedFolders });
    };

    const folderIconColor = isTopLevel ? '#754DCF' : '#C6B8E7';

    const areSubfoldersDisplayed = isExpanded && folders?.length > 0;

    useEffect(() => {
        // if user drag a block to a collapsed section (sections is hovered for 1s) expand it
        let stopTimeout;
        if (isDndOver && !isExpanded) {
            stopTimeout = setSyncTimeout(handleToggleButtonClick, 1000);
        }

        return () => {
            if (stopTimeout) {
                stopTimeout();
            }
        };
    }, [isDndOver, isExpanded]);

    const fetchNextLevelFolders = async () => {
        try {
            const currentFolderLocation = [...prevFoldersLocation?.map(({ id }) => id), id];

            for (const subFolder of folder.folders) {
                if (subFolder.folders) {
                    continue;
                }

                client
                    .get(API.ROUTES.knowledge.folder, {
                        params: {
                            parent: subFolder.id,
                            pagination: false,
                            ordering: SORT_TYPES.alphabeticalByLabel,
                        },
                    })
                    .then(({ data }) => {
                        handleUpdateFoldersState({
                            setFolders: setSidePanelFolders,
                            location: [...(currentFolderLocation ?? []), subFolder.id],
                            cb: (folder) => ({ ...folder, folders: data }),
                        });
                    })
                    .catch((e) => {});
            }
        } catch (e) {}
    };

    useEffect(() => {
        if (isExpanded) {
            void fetchNextLevelFolders();
        }
    }, [isExpanded]);

    useEffect(() => {
        if (isDragging && isMoreOptionsPopupOpened) {
            closeMoreOptions();
        }
    }, [isDragging]);

    const handleToggleButtonClick = (e) => {
        e?.stopPropagation();
        e?.preventDefault();
        const locationIdsArray = [...prevFoldersLocation.map((loc) => loc.id), id];
        toggleFolderExpand(locationIdsArray);
    };

    const currentFolderLocation = [...prevFoldersLocation, { id, label }];

    const handleFolderClick = (e) => {
        if (isMoreOptionsPopupOpened) return;

        if (!isExpanded) {
            handleToggleButtonClick(e);
        }
        setLocation(currentFolderLocation);
        navigate(`/docs/folder/${id}`);
    };

    const openDuplicatedFolder = ({ location }) => {
        setLocation(location);

        handleExpandSidePanelFolder({
            location: location.map((loc) => loc.id),
            setExpandedFolders,
        });
    };

    const isHighlighted = folderIdFromParams === id || isMoreOptionsPopupOpened;

    const buttonClassName = classNames(
        'p-2 h-[40px] w-full flex items-center rounded-2 hover:bg-neutral-50 focus:outline-none shadow-inset-2 group',
        {
            'bg-white': !isHighlighted && !isDndOver,
            'bg-neutral-50': isHighlighted || isDndOver,
            'shadow-purple-500': isDndOver,
            'shadow-transparent': !isDndOver,
            'opacity-40': isDragging,
            'opacity-100': !isDragging,
        }
    );

    const moreOptionsButtonClassName = classNames('pr-2 focus:outline-0 group-hover:block', {
        hidden: !isMoreOptionsPopupOpened,
        block: isMoreOptionsPopupOpened,
    });

    return (
        <li className="max-w-full flex flex-col gap-1">
            <button
                onClick={handleFolderClick}
                className={buttonClassName}
                ref={ref}
                style={{
                    transition: 'box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out',
                }}
            >
                <div className="block" onClick={handleToggleButtonClick}>
                    <SvgIcon icon={Icon} size="medium" color="#000000" />
                </div>
                <FolderFillIcon width={20} height={20} color={folderIconColor} />
                <p className="flex-1 pl-2 truncate font-body text-body-regular-s text-neutral-500 text-start">
                    {label}
                </p>

                <div className="relative">
                    <button className={moreOptionsButtonClassName} onClick={openMoreOptions}>
                        <SvgIcon color="#000000" icon={More2FillIcon} size="large" />
                    </button>

                    {isMoreOptionsPopupOpened && (
                        <DocCardMoreOptionsPopup
                            contentType={CONTENT_TYPE.folder}
                            data={{ id, label, location: currentFolderLocation }}
                            parentLocation={prevFoldersLocation}
                            setActionModal={setActionModal}
                            setSuccessAlert={setSuccessAlert}
                            setErrorAlert={setErrorAlert}
                            onNavigateToFolderView={openDuplicatedFolder}
                            width={270}
                            position="top-7 right-0"
                            onClose={closeMoreOptions}
                        />
                    )}
                </div>
            </button>

            <div className={!areSubfoldersDisplayed ? 'hidden' : 'block'}>
                <ul className="flex flex-col gap-1 max-w-full pl-4">
                    {folders?.map((subFolder) => {
                        const subFolderExpandedState = currentFolderExpandedState?.[subFolder.id];

                        return (
                            <DraggableFolderNavItem
                                key={subFolder.id}
                                folder={subFolder}
                                currentFolderExpandedState={subFolderExpandedState}
                                prevFoldersLocation={currentFolderLocation}
                                setActionModal={setActionModal}
                            />
                        );
                    })}
                </ul>
            </div>

            {isExpanded && !folders && is_parent && (
                <div>
                    <Loading withText={false} size="small" />
                </div>
            )}

            {successAlert && (
                <Alert
                    status="positive"
                    message={successAlert.message}
                    icon={CheckLineIcon}
                    description={successAlert.description}
                    position="top-center"
                    autoCloseInMS={5000}
                    handleClose={() => setSuccessAlert(null)}
                />
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </li>
    );
};

const ForwardedFolderNavItem = forwardRef(FolderNavItem);

export default ForwardedFolderNavItem;
